import clsx from 'clsx';
import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    type BadgeKind,
    Button,
    LoadingPanel,
    SegmentedControl,
    type TabProps,
} from '@gitbook/spine-react';

import { SidePanelExpandableContext } from './context';

export type SidePanelTabProps<TabKey extends string = string> = {
    readonly key: TabKey;
    readonly label: string;
} & Omit<TabProps, 'active' | 'onPress' | 'children'>;

export interface SidePanelSheetProps<TabKey extends string = string> {
    title: React.ReactNode;

    /** badge to be displayed next to the title */
    titleBadgeLabel?: string;
    titleBadgeKind?: BadgeKind;

    /** Tabs shown in the sidepanel header */
    tabs?: readonly SidePanelTabProps<TabKey>[];
    onSelectedTabChange?: (tab: TabKey) => void;
    selectedTab?: TabKey;

    /** Filters are displayed next to the tabs if there are any, else they are displayed in the header */
    filters?: React.ReactNode;

    /** Buttons shown in the sidepanel header */
    headerButtons?: React.ReactNode[];

    /**
     * If true, the sidepanel can be expanded by the user to be larger and over the content.
     */
    expandable?: boolean;

    onClose: () => void;
    children: React.ReactNode;
}

/**
 * Component to use to render a structured UI in a <SidePanel />.
 */
export function SidePanelSheet<TabKey extends string = string>(
    props: SidePanelSheetProps<TabKey>
): React.ReactElement {
    const {
        title,
        titleBadgeKind,
        titleBadgeLabel,
        onClose,
        children,
        tabs,
        onSelectedTabChange,
        selectedTab,
        headerButtons,
        expandable = false,
        filters,
    } = props;
    const hasTabs = !!(tabs && tabs.length > 0);
    const { expanded, setExpanded } = React.useContext(SidePanelExpandableContext);

    React.useEffect(() => {
        if (!expandable) {
            setExpanded?.(false);
        }
    }, [expandable, setExpanded]);

    return (
        <>
            <div
                className={clsx(
                    'flex h-toolbar-header items-center gap-2 py-4 pr-3 pl-4',
                    expandable && 'pl-2',
                    !hasTabs && 'border-b'
                )}
            >
                <div className="flex flex-1 items-center gap-2">
                    <h3 className="text-ui-heading-small">{title}</h3>
                    {titleBadgeLabel ? (
                        <Badge
                            size="xsmall"
                            kind={titleBadgeKind ?? 'info'}
                            label={titleBadgeLabel}
                        />
                    ) : null}
                </div>

                <div className="flex items-center gap-1">
                    {headerButtons?.map((item, index) => (
                        <React.Fragment key={index}>{item}</React.Fragment>
                    ))}
                    {!hasTabs && filters ? filters : null}
                    {expandable ? (
                        <Button
                            kind="blank"
                            label={expanded ? 'Collapse panel' : 'Expand panel'}
                            leadingIcon={expanded ? Icon.PanelLeftOpen : Icon.PanelLeftClose}
                            size="medium"
                            iconOnly
                            onPress={() => {
                                setExpanded?.((prev) => !prev);
                            }}
                        />
                    ) : null}
                    <Button
                        kind="blank"
                        label="Close"
                        leadingIcon={Icon.X}
                        size="medium"
                        onPress={onClose}
                        iconOnly
                    />
                </div>
            </div>

            {hasTabs ? (
                <div className="flex w-full items-center justify-between gap-2 border-b px-4 pb-2">
                    <SegmentedControl
                        options={tabs.map((tab) => ({
                            value: tab.key,
                            label: tab.label,
                        }))}
                        grow
                        size="small"
                        active={selectedTab}
                        onChange={(key) => onSelectedTabChange?.(key as TabKey)}
                    />
                    {filters ? filters : null}
                </div>
            ) : null}

            {/* Ensure the container is `relative` so that any focus in this container remains trapped within it and doesn't cause the entire document to shift and offset */}
            <div className="relative flex flex-1 flex-col overflow-y-auto">
                <React.Suspense fallback={<LoadingPanel size="medium" />}>
                    {children}
                </React.Suspense>
            </div>
        </>
    );
}
